/**
 * ENTRAID-350 "start small": the Essentials preset and what every Microsoft Graph permission is used for.
 *
 * A security-conscious buyer asks for least privilege and a justification per permission. Onboarding therefore offers
 * three choices for the data sources: Essentials (the default), All data sources, and Customize. Essentials is not a
 * hand-picked list: it is derived here from two catalogs, so it follows them when they change.
 *
 *   1. The security checks catalog (lambda/security-checks.mjs). CHECK_REQUIRED_SOURCES below says which data sources each
 *      check reads. It is a copy pinned by tests/permission-presets-checks.test.mjs, which derives the same map from the
 *      checks' own inputs and fails on any drift (a new check, or a check that reads a new source).
 *   2. The data source catalog and its permissions (portal/shared/collectors.mjs, portal/shared/permission-map.mjs).
 *
 * The rule (docs/PORTAL-ARCHITECTURE.md, "Essentials preset"):
 *
 *   Essentials permissions = every permission a security check needs, minus the permissions that only ever return data on
 *   a Microsoft Entra ID P2 tenant, plus what Watch core reads, plus the permissions of ESSENTIAL_EXTRA_SOURCES (an explicit,
 *   documented list: group memberships and on-premises sync, free/P1 directory context for analytics at low privilege;
 *   product owner decision 2026-10-25). The onGuard score is the weighted result of the checks, so this is also everything
 *   the score needs on a tenant without P2.
 *   Essentials sources     = every data source whose permissions are all inside Essentials (they cost nothing more).
 *
 * Still left out on purpose: SharePoint sites (Sites.Read.All, least privilege), the Microsoft 365 usage reports
 * (Reports.Read.All) and every Microsoft Entra ID P2 source.
 * Every other permission is an add-on the customer turns on later from Data collection: the app manifest is updated and
 * the admin consents again (routes/customer-permissions.mjs). Pure data and functions; no I/O.
 */

import { COLLECTOR_CATEGORIES, SOURCE_LICENSES, LICENSE_LABELS } from './collectors.mjs';
import { ALL_PERMISSIONS, CORE_PERMISSION, PERMISSION_JUSTIFICATIONS, SOURCE_REQUIRED_PERMISSIONS, normalizeSelection } from './permission-map.mjs';

/** The data sources each security check reads (from the inputs of its `run`). Pinned by a repository guard test. */
export const CHECK_REQUIRED_SOURCES = Object.freeze(Object.fromEntries(Object.entries({
  'IDENT-001': ['users'],
  'IDENT-002': ['users'],
  'IDENT-003': ['role-assignments', 'user-roles'],
  'IDENT-004': ['user-roles', 'users'],
  'AUTH-001': ['registration-details'],
  'AUTH-002': ['auth-methods'],
  'AUTH-003': ['registration-details'],
  'AC-001': ['conditional-access'],
  'AC-002': ['conditional-access'],
  'AC-003': ['conditional-access'],
  'AC-004': ['named-locations'],
  'AC-005': ['eligible-roles'],
  'APP-001': ['app-registrations'],
  'APP-002': ['oauth2-grants'],
  'APP-003': ['service-principals'],
  'APP-004': ['app-registrations'],
  'MON-001': ['risk-detections'],
  'MON-002': ['risky-users'],
  'MON-003': ['secure-score'],
  'MON-004': ['recommendations'],
  'MON-005': ['devices'],
  'IDENT-005': ['users'],
  'IDENT-006': ['users'],
  'AUTH-004': ['auth-methods'],
  'AUTH-005': ['auth-methods'],
  'AC-006': ['conditional-access'],
  'AC-007': ['conditional-access'],
  'AC-008': ['conditional-access'],
  'APP-005': ['oauth2-grants'],
  'APP-006': ['service-principals'],
  'MON-006': ['devices'],
  'IDENT-007': ['subscribed-skus', 'user-licenses', 'users'],
  'AC-009': ['conditional-access', 'organization'],
  'AUTH-006': ['domains'],
  'MON-007': ['sign-ins'],
  'IDENT-008': ['groups'],
  'AC-010': ['conditional-access'],
  'AC-011': ['organization'],
  'APP-007': ['organization'],
  'APP-008': ['app-registrations'],
  'IDENT-009': ['user-roles'],
  'MON-008': ['sign-ins'],
  'AUTH-007': ['password-protection'],
  'APP-009': ['admin-consent-policy'],
  'AUTH-008': ['authentication-methods-policy'],
  'AUTH-009': ['authentication-methods-policy'],
  'MON-009': ['audit-log-retention'],
  'AC-012': ['access-reviews'],
  'AC-013': ['role-management-policies'],
  'IDENT-010': ['conditional-access', 'users'],
  'IDENT-011': ['user-roles', 'users'],
  'IDENT-012': ['user-licenses', 'user-roles'],
  'IDENT-013': ['user-roles', 'users'],
  'IDENT-014': ['groups'],
  'AC-014': ['conditional-access'],
  'AC-015': ['conditional-access', 'eligible-roles', 'user-roles'],
  'AC-016': ['conditional-access'],
  'AC-017': ['conditional-access', 'eligible-roles', 'user-roles'],
  'AC-018': ['conditional-access'],
  'AC-020': ['organization'],
  'AC-021': ['conditional-access'],
  'AUTH-010': ['password-protection'],
  'AUTH-011': ['auth-methods', 'user-roles'],
  'AUTH-012': ['organization', 'password-protection'],
  'APP-010': ['organization'],
  'APP-011': ['organization'],
  'AUTH-013': ['authentication-methods-policy'],
  'AUTH-014': ['authentication-methods-policy'],
  'AUTH-015': ['authentication-methods-policy', 'certificate-authorities'],
  'AC-019': ['eligible-roles', 'user-roles'],
  'ARV-004': ['role-assignments'],
}).map(([id, list]) => [id, Object.freeze([...list])])));

/**
 * What Watch core reads with the customer's app (watch-lambda): directory audit records (AuditLog.Read.All), recipients
 * and people context (User.Read.All), group names and owners (Group.Read.All), applications, service principals and
 * domains (Directory.Read.All), and the tenant's named locations for the sign-in network context (Policy.Read.All). The
 * risky sign-in notice (IdentityRiskEvent.Read.All, Microsoft Entra ID P2) is an add-on, not core.
 */
export const WATCH_CORE_PERMISSIONS = Object.freeze(['AuditLog.Read.All', 'Directory.Read.All', 'Group.Read.All', 'Policy.Read.All', 'User.Read.All']);

/** Watch add-ons by permission (shown as "what it unlocks"). */
const WATCH_ADDONS = Object.freeze({ 'IdentityRiskEvent.Read.All': 'Watch: the risky sign-in notice' });

const SOURCES_BY_PERMISSION = Object.freeze(Object.fromEntries(ALL_PERMISSIONS.map((p) => [
  p,
  Object.freeze(Object.keys(SOURCE_REQUIRED_PERMISSIONS).filter((s) => SOURCE_REQUIRED_PERMISSIONS[s].includes(p)).sort()),
])));

/** The license a permission needs to return any data: the license of its sources when they ALL need the same one, else null. */
export function permissionLicense(permission) {
  const sources = SOURCES_BY_PERMISSION[permission] ?? [];
  if (sources.length === 0) return null;
  const levels = new Set(sources.map((s) => SOURCE_LICENSES[s] ?? null));
  return levels.size === 1 ? [...levels][0] : null;
}

/**
 * Data sources added to Essentials by decision, beyond what the checks and Watch core need (product owner decision
 * 2026-10-25). Reason: free/P1 directory context for analytics (who belongs to which group, whether the tenant syncs from
 * on-premises Active Directory) at low privilege. Keep this list short and explicit; a source needing P2 never belongs here.
 */
export const ESSENTIAL_EXTRA_SOURCES = Object.freeze(['group-memberships', 'on-premises-sync']);

const checkPermissions = (checkId) => [...new Set(CHECK_REQUIRED_SOURCES[checkId].flatMap((s) => SOURCE_REQUIRED_PERMISSIONS[s]))];

/** The Essentials permission list (sorted). See the rule at the top of this file. */
export const ESSENTIAL_PERMISSIONS = Object.freeze((() => {
  const set = new Set([CORE_PERMISSION, ...WATCH_CORE_PERMISSIONS]);
  for (const id of Object.keys(CHECK_REQUIRED_SOURCES)) {
    for (const p of checkPermissions(id)) if (permissionLicense(p) !== 'P2') set.add(p);
  }
  for (const source of ESSENTIAL_EXTRA_SOURCES) for (const p of SOURCE_REQUIRED_PERMISSIONS[source]) set.add(p);
  return [...set].sort();
})());

/** Every data source whose permissions are all inside Essentials (sorted). */
export const ESSENTIAL_SOURCES = Object.freeze(Object.keys(SOURCE_REQUIRED_PERMISSIONS)
  .filter((s) => SOURCE_REQUIRED_PERMISSIONS[s].every((p) => ESSENTIAL_PERMISSIONS.includes(p)))
  .sort());

/** The Essentials selection, in the form POST /customers/{id}/permissions accepts. */
export const ESSENTIALS_SELECTION = Object.freeze({ mode: 'custom', sources: ESSENTIAL_SOURCES });

export const PRESETS = Object.freeze(['essentials', 'all', 'custom']);

/** Which preset a selection is: 'all', 'essentials' (exactly the Essentials sources) or 'custom'. Invalid input is 'custom'. */
export function presetOf(selection) {
  const checked = normalizeSelection(selection);
  if (!checked.ok) return 'custom';
  if (checked.selection.mode === 'all') return 'all';
  const s = checked.selection.sources;
  return s.length === ESSENTIAL_SOURCES.length && s.every((id, i) => id === ESSENTIAL_SOURCES[i]) ? 'essentials' : 'custom';
}

/** The selection for a preset ('custom' takes the given sources). Throws on an unknown preset. */
export function selectionForPreset(preset, customSources = []) {
  if (preset === 'essentials') return { mode: 'custom', sources: [...ESSENTIAL_SOURCES] };
  if (preset === 'all') return { mode: 'all', sources: [] };
  if (preset === 'custom') return { mode: 'custom', sources: [...new Set(customSources)].sort() };
  throw new Error(`unknown preset: ${preset}`);
}

const LABELS = Object.freeze(Object.fromEntries(COLLECTOR_CATEGORIES.flatMap((c) => c.collectors).map((c) => [c.id, c.label])));

/**
 * What one permission is for, in plain language, for the table shown before the Microsoft consent:
 *   { permission, readOnly: true, why, sources: [{ id, label }], checks: [check ids], watch: 'core' | string | null,
 *     license: 'Microsoft Entra ID P2' | null, essential }
 * Every permission onGuard requests is an application permission named *.Read.* (read only); `readOnly` is computed, so a
 * write permission could never be shown as read only.
 */
export function permissionUsage(permission) {
  if (!ALL_PERMISSIONS.includes(permission)) throw new Error(`unknown permission: ${permission}`);
  const sources = SOURCES_BY_PERMISSION[permission];
  const checks = Object.keys(CHECK_REQUIRED_SOURCES).filter((id) => checkPermissions(id).includes(permission));
  const level = permissionLicense(permission);
  return {
    permission,
    readOnly: /\.Read\./.test(`${permission}.`),
    why: PERMISSION_JUSTIFICATIONS[permission],
    sources: sources.map((id) => ({ id, label: LABELS[id] ?? id })),
    checks,
    watch: WATCH_CORE_PERMISSIONS.includes(permission) ? 'core' : (WATCH_ADDONS[permission] ?? null),
    license: level ? LICENSE_LABELS[level] : null,
    essential: ESSENTIAL_PERMISSIONS.includes(permission),
  };
}

/** One sentence: which features a permission powers ("12 security checks, Watch, and these data sources: ..."). */
export function usageSummary(permission) {
  const u = permissionUsage(permission);
  const parts = [];
  if (u.checks.length > 0) parts.push(`${u.checks.length} security check${u.checks.length === 1 ? '' : 's'} (${u.checks.join(', ')})`);
  if (u.watch === 'core') parts.push('Watch alerts');
  else if (u.watch) parts.push(u.watch);
  if (u.sources.length > 0) parts.push(`data sources: ${u.sources.map((s) => s.label).join(', ')}`);
  const text = parts.join('; ');
  return u.license ? `${text}. Returns data only with ${u.license}.` : `${text}.`;
}

/** The permissions outside a list, each with its usage: what adding it later would unlock. */
export function addOnsBeyond(permissions) {
  const have = new Set(permissions ?? []);
  return ALL_PERMISSIONS.filter((p) => !have.has(p)).map(permissionUsage);
}
